import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import '../css/CvControleAdmin.css';
import '../css/PageWithSidebar.css';

const STATUS_LABELS = {
  'geen-problemen': { label: 'Geen problemen', className: 'status-ok', icon: '✅' },
  'probleem': { label: 'Probleem', className: 'status-probleem', icon: '⚠️' },
  'twijfel': { label: 'Twijfel', className: 'status-twijfel', icon: '❓' },
  none: { label: 'Niet gereageerd', className: 'status-none', icon: '—' }
};

const ONDERDEEL_LABELS = {
  'radiator': 'Radiator',
  'radiatorkraan': 'Radiatorkraan',
  'cv-leiding': 'Cv-leiding'
};

const AFHANDELING_OPTIONS = [
  { value: 'nieuw', label: 'Nieuw' },
  { value: 'ingepland', label: 'Ingepland' },
  { value: 'afgehandeld', label: 'Afgehandeld' }
];

const FILTERS = [
  { value: 'all', label: 'Alles' },
  { value: 'openstaand', label: 'Openstaand' },
  { value: 'probleem', label: 'Probleem' },
  { value: 'twijfel', label: 'Twijfel' },
  { value: 'geen-problemen', label: 'Geen problemen' },
  { value: 'none', label: 'Niet gereageerd' }
];

const onderdelenTekst = (onderdelen) =>
  (onderdelen || []).map(o => ONDERDEEL_LABELS[o] || o).join(', ');

const CvControleAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Lokale notitie-invoer per melding, zodat typen niet elke toetsaanslag opslaat
  const [notities, setNotities] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/cv-controle/admin/all');
      setData(res.data);
      const initieleNotities = {};
      for (const row of res.data.rows) {
        if (row.melding) initieleNotities[row.melding._id] = row.melding.notitieBestuur || '';
      }
      setNotities(initieleNotities);
    } catch (err) {
      console.error('Error fetching cv-controle meldingen:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAfhandeling = async (id, afhandeling) => {
    try {
      await api.patch(`/api/cv-controle/admin/${id}`, { afhandeling });
      fetchData();
    } catch (err) {
      alert('Fout bij bijwerken van de status');
    }
  };

  const handleNotitieOpslaan = async (id) => {
    try {
      await api.patch(`/api/cv-controle/admin/${id}`, { notitieBestuur: notities[id] || '' });
      fetchData();
    } catch (err) {
      alert('Fout bij opslaan van de notitie');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze melding wilt verwijderen?')) return;
    try {
      await api.delete(`/api/cv-controle/admin/${id}`);
      fetchData();
    } catch (err) {
      alert('Fout bij verwijderen');
    }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.rows;
    if (filter === 'none') return data.rows.filter(r => !r.hasResponded);
    if (filter === 'openstaand') {
      return data.rows.filter(r =>
        r.melding && r.melding.status !== 'geen-problemen' && r.melding.afhandeling !== 'afgehandeld'
      );
    }
    return data.rows.filter(r => r.melding && r.melding.status === filter);
  }, [data, filter]);

  const exporteerCsv = () => {
    const kolommen = [
      'Huisnummer', 'Status', 'Naam (melding)', 'Naam (bewonerslijst)', 'E-mail', 'Telefoon',
      'Onderdelen', 'Locatie', 'Omschrijving', 'Zelf herstel', 'Afhandeling', 'Notitie bestuur', 'Ingediend op'
    ];
    const escape = (waarde) => `"${String(waarde ?? '').replace(/"/g, '""')}"`;

    const regels = filteredRows.map(row => {
      const m = row.melding;
      return [
        row.huisnummer,
        m ? STATUS_LABELS[m.status].label : STATUS_LABELS.none.label,
        m ? m.naam : '',
        row.residentNaam,
        m ? m.email : '',
        m ? m.telefoon : '',
        m ? onderdelenTekst(m.onderdelen) : '',
        m ? m.locatie : '',
        m ? m.omschrijving : '',
        m && m.zelfHerstel ? 'Ja' : '',
        m ? m.afhandeling : '',
        m ? m.notitieBestuur : '',
        m && m.submittedAt ? new Date(m.submittedAt).toLocaleString('nl-NL') : ''
      ].map(escape).join(';');
    });

    // BOM zodat Excel de accenten goed toont
    const csv = '﻿' + [kolommen.map(escape).join(';'), ...regels].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cv-controle-meldingen.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <BeheerdersportaalSidebar>
      <div className="cvcontrole-admin">
        <div className="cvcontrole-admin-header">
          <div>
            <h1>CV-controle meldingen</h1>
            <p className="admin-subtitle">
              Controle van radiatoren, radiatorkranen en cv-leidingen vóór het vullen van de installatie
            </p>
          </div>
          <button type="button" className="btn-export" onClick={exporteerCsv} disabled={!data}>
            Exporteer CSV
          </button>
        </div>

        {loading ? (
          <p>Laden...</p>
        ) : !data ? (
          <p>Geen data beschikbaar.</p>
        ) : (
          <>
            <div className="admin-stats">
              <div className="stat-card stat-probleem">
                <div className="stat-number">{data.stats.probleem}</div>
                <div className="stat-label">Probleem</div>
              </div>
              <div className="stat-card stat-twijfel">
                <div className="stat-number">{data.stats.twijfel}</div>
                <div className="stat-label">Twijfel</div>
              </div>
              <div className="stat-card stat-openstaand">
                <div className="stat-number">{data.stats.openstaand}</div>
                <div className="stat-label">Nog op te volgen</div>
              </div>
              <div className="stat-card stat-ok">
                <div className="stat-number">{data.stats.geenProblemen}</div>
                <div className="stat-label">Geen problemen</div>
              </div>
              <div className="stat-card stat-none">
                <div className="stat-number">{data.stats.nietGereageerd}</div>
                <div className="stat-label">Niet gereageerd</div>
              </div>
              <div className="stat-card stat-total">
                <div className="stat-number">{data.stats.totaalActief}</div>
                <div className="stat-label">Totaal woningen</div>
              </div>
            </div>

            <div className="filter-bar">
              {FILTERS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  className={`filter-btn ${filter === f.value ? 'active' : ''}`}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Huisnr</th>
                    <th>Status</th>
                    <th>Melder</th>
                    <th>Contact</th>
                    <th>Onderdeel / locatie</th>
                    <th>Omschrijving</th>
                    <th>Opvolging</th>
                    <th>Ingediend op</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(row => {
                    const m = row.melding;
                    const status = STATUS_LABELS[m ? m.status : 'none'];
                    return (
                      <tr key={row.huisnummer} className={row.hasResponded ? '' : 'row-no-response'}>
                        <td><strong>{row.huisnummer}</strong></td>
                        <td>
                          <span className={`status-badge ${status.className}`}>
                            <span className="status-badge-icon">{status.icon}</span>
                            {status.label}
                          </span>
                        </td>
                        <td>
                          {m ? m.naam : '—'}
                          {row.residentNaam && (!m || row.residentNaam !== m.naam) && (
                            <div className="cell-sub">{row.residentNaam}</div>
                          )}
                        </td>
                        <td className="cell-contact">
                          {m && m.email ? <a href={`mailto:${m.email}`}>{m.email}</a> : (row.residentEmail || '—')}
                          {m && m.telefoon && <div className="cell-sub">{m.telefoon}</div>}
                        </td>
                        <td>
                          {m && onderdelenTekst(m.onderdelen) ? onderdelenTekst(m.onderdelen) : '—'}
                          {m && m.locatie && <div className="cell-sub">{m.locatie}</div>}
                          {m && m.zelfHerstel && (
                            <div className="tag-zelfherstel">Regelt zelf installateur</div>
                          )}
                        </td>
                        <td className="cell-omschrijving">
                          {m && m.omschrijving ? m.omschrijving : '—'}
                        </td>
                        <td className="cell-opvolging">
                          {m ? (
                            <>
                              <select
                                value={m.afhandeling}
                                onChange={(e) => handleAfhandeling(m._id, e.target.value)}
                                className={`afhandeling-select afhandeling-${m.afhandeling}`}
                              >
                                {AFHANDELING_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              <textarea
                                className="notitie-input"
                                value={notities[m._id] ?? ''}
                                onChange={(e) => setNotities({ ...notities, [m._id]: e.target.value })}
                                onBlur={() => {
                                  if ((notities[m._id] || '') !== (m.notitieBestuur || '')) {
                                    handleNotitieOpslaan(m._id);
                                  }
                                }}
                                placeholder="Notitie bestuur..."
                                rows={2}
                                maxLength={2000}
                              />
                            </>
                          ) : '—'}
                        </td>
                        <td>
                          {m && m.submittedAt
                            ? new Date(m.submittedAt).toLocaleString('nl-NL')
                            : '—'}
                        </td>
                        <td>
                          {m && (
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => handleDelete(m._id)}
                            >
                              Verwijder
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredRows.length === 0 && (
              <p className="no-results">Geen resultaten voor dit filter.</p>
            )}
          </>
        )}
      </div>
    </BeheerdersportaalSidebar>
  );
};

export default CvControleAdmin;
